import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { ImageLightbox } from '../ImageLightbox';
import { useNodeResize } from './useNodeResize';
import { GalleryPickerModal, type GalleryPickerResult } from '../../Gallery/GalleryPickerModal';
import { Image, X, CloudUpload, Upload } from 'lucide-react';

interface MediaNodeDataProps {
  imageUrl?: string;
  originalName?: string;
  uploading?: boolean;
  uploadError?: string;
  nodeWidth?: number;
  onUpload?: (id: string, file: File, localUrl: string) => void;
  onDelete?: (id: string) => void;
  onWidthChange?: (id: string, width: number) => void;
  onSelectFromGallery?: (id: string, imageUrl: string, title: string) => void;
}

export function MediaNode({ id, data: rawData, selected }: NodeProps) {
  const data = rawData as MediaNodeDataProps;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [galleryPickerOpen, setGalleryPickerOpen] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const { nodeWidth, resizeHandleProps } = useNodeResize({
    width: data.nodeWidth as number | undefined,
    minWidth: 180,
    maxWidth: 600,
    onWidthChange: (w) => data.onWidthChange?.(id, w),
  });

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const localUrl = URL.createObjectURL(file);
    if (data.onUpload) {
      data.onUpload(id, file, localUrl);
    }
  }, [id, data]);

  const handleGallerySelect = useCallback((result: GalleryPickerResult) => {
    if (data.onSelectFromGallery) {
      data.onSelectFromGallery(id, result.url, result.title);
    }
  }, [id, data]);

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        const localUrl = URL.createObjectURL(file);
        if (data.onUpload) {
          data.onUpload(id, file, localUrl);
        }
      }
    }
  }, [id, data]);

  return (
    <>
      <div
        className={`bg-white rounded-lg border-2 shadow-md min-w-[180px] relative ${!nodeWidth ? 'max-w-[240px]' : ''} ${selected ? 'border-primary-500' : 'border-gray-200'}`}
        style={nodeWidth ? { width: nodeWidth } : undefined}
      >
        <div className="px-3 py-2 bg-accent-50 border-b border-accent-100 rounded-t-lg flex items-center gap-2">
          <Image className="w-4 h-4 text-accent-600" />
          <span className="text-xs font-semibold text-accent-700">Media</span>
          {selected && (
            <button
              onClick={(e) => { e.stopPropagation(); data.onDelete?.(id); }}
              className="ml-auto text-gray-400 hover:text-red-500 p-0.5"
              aria-label="Delete node"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {data.imageUrl ? (
          <div className="p-2 relative">
            <img
              src={data.imageUrl}
              alt={data.originalName || 'Media'}
              className="w-full h-auto rounded-md cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => setLightboxOpen(true)}
            />
            {data.uploading && (
              <div className="absolute inset-2 flex items-center justify-center bg-black/30 rounded-md">
                <span className="text-xs text-white font-medium">Uploading...</span>
              </div>
            )}
            <p className="text-[10px] font-mono text-gray-500 mt-1 truncate px-1">{data.originalName}</p>
            {data.uploadError && (
              <div className="px-1 pt-1">
                <p className="text-xs text-red-500">Upload failed: {data.uploadError}</p>
                <button
                  className="text-xs text-primary-600 underline mt-1"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Try again
                </button>
              </div>
            )}
          </div>
        ) : (
          <div
            className={`p-4 flex flex-col items-center justify-center min-h-[140px] transition-colors ${
              isDragOver
                ? 'bg-primary-50 border-2 border-dashed border-primary-400'
                : 'bg-gray-50'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {isDragOver ? (
              <>
                <CloudUpload className="w-8 h-8 text-primary-400 mb-2" />
                <span className="text-xs text-primary-600 font-medium">Drop image here</span>
              </>
            ) : (
              <>
                {/* Upload button */}
                <button
                  className="w-full flex items-center justify-center gap-2 px-3 py-1.5 mb-2 rounded-md bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="w-4 h-4 text-gray-500" />
                  <span className="text-xs text-gray-600">Upload</span>
                </button>

                {/* Gallery button */}
                <button
                  className="w-full flex items-center justify-center gap-2 px-3 py-1.5 mb-2 rounded-md bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                  onClick={() => setGalleryPickerOpen(true)}
                >
                  <Image className="w-4 h-4 text-gray-500" />
                  <span className="text-xs text-gray-600">Gallery</span>
                </button>

                {/* Drop hint */}
                <span className="text-xs text-gray-400">or drop image here</span>
              </>
            )}
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />
        <Handle type="source" position={Position.Right} className="!bg-accent-500 !w-3 !h-3" />
        <div {...resizeHandleProps} />
        {lightboxOpen && data.imageUrl && (
          <ImageLightbox src={data.imageUrl} alt={data.originalName || 'Media'} onClose={() => setLightboxOpen(false)} />
        )}
      </div>

      {/* Gallery Picker Modal - rendered via portal to escape node clipping */}
      {galleryPickerOpen && createPortal(
        <GalleryPickerModal
          isOpen={galleryPickerOpen}
          onClose={() => setGalleryPickerOpen(false)}
          onSelect={handleGallerySelect}
        />,
        document.body
      )}
    </>
  );
}

// Also export as UploadNode for backwards compatibility
export { MediaNode as UploadNode };
